define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"csi",
	"csi/_base/underscore",
	"csi/constants",
	"csi/def/Heading",
	"csi/heading/Spec",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/table/matrix/Path",
	"csi/types",
	"csi/xml",
	"./_SizeChartRevision",
	"./SizeChartSizesMixin"
], function(declare, lang, domClass, csi, _, constants, Heading, Spec, html, locale, reflection, PathMatrix, types, xml, _SizeChartRevision, SizeChartSizesMixin) {

	const CSS_UNSELECTED = "csi-size-unselected";
	const CSS_SIZE_BASE = "csi-size-base";
	var DIM2_HEADER_INDEX = -1;
	var _spacerHeading = new Heading();
	_spacerHeading.columnWidth = types.COLUMN_WIDTH_MIN;
	_spacerHeading.m_value = "";

	return declare([PathMatrix, SizeChartSizesMixin], {
		restrictQuery: true,

		// Variable for caching to improve performance
		_sizeChartRevision: null,   // parent SizeChartRevision node

		/** @property {_SizeChartRevision} _sizeChartRevisionData - object which provides easy access of 2Dim Sizes data */
		_sizeChartRevisionData: null,
		_isTwoDim: false,    // boolean true if the parent SizeChartRevision has two dimensional sizes
		_sizes: null,
		_dim1Sizes: null,
		_dim2Sizes: null,
		_dim1Type: null,
		_dim2Type: null,

		_unselectedDim2: [], // fully unselected matrix row nodes. For internal module use only
		_unselectedDim2Indices: [], // fully unselected matrix row indices. For internal module use only

		addDependencyPaths: function(/*Heading*/h, /*String[]*/paths) {
			// summary: add any dependency paths necessary to process matrix
			paths.push("Child:SizeChartDimension1Sizes");
			paths.push("Child:SizeChartDimension2Sizes");
			paths.push("Child:Sizes");
			paths.push("Child:Items/Child:Original");
			this.inherited(arguments);
		},

		/*
		 * Override the same function on the base class - csi.table.matrix.Path#getColumnSpecs
		 * @param{Context} ctx
		 * @param{Heading} h
		 * @return{Spec[]} - column specifications for matrix
		 */
		getColumnSpecs: function(ctx, h) {
			var view = ctx.view;
			var specs = this.inherited(arguments);
			var selectedSubSizeRange = view.getSelectedSubSizeRange();
			var subSizeRanges = selectedSubSizeRange ? [selectedSubSizeRange] : view.getSubSizeRanges();
			var bBaseSize = view.sizeChartPrintBaseSize;

			this._cacheSizesVectors(ctx);
			// Reset the 2-d spec indices so that it will be recalculated when view refresh
			view.twoDimSpecIndices = null;

			if (bBaseSize) {
				// if displaying base size only, update index (indices)
				if (this._isTwoDim) {
					// In 2Dim SizeChart, only simple SizeRange is supported and the corresponding Dim1 size on the base size column is displayed
					specs[0].setParam("index", this._sizeChartRevisionData.baseSizeDim1Index, true);
				} else {
					_.each(subSizeRanges, function(sr, i) {
						specs[i].setParam("index", sr.BaseSizeIndex, true);
					});
				}
			} else {
				_.each(specs, function(spec) {
					spec.setParam("index", view.getViewNode().SizeChartDimension1Sizes.indexOf(spec.columnUrl), true);
				});
			}

			// Add light grey background for unselected sizes
			// Delete unselected columns if not DisplayAllSizes display mode
			var toDelete = [];
			const selectedSizes = this._sizeChartRevision.SelectedSizes;
			const baseSizes = view.getBaseSizes();
			if (!this._isTwoDim) {
				_.each(specs, function(spec, idx) {
					var columnUrl = spec.columnUrl;
					if (!selectedSizes.includes(columnUrl)) {
						if (!view.sizeChartDisplayAllSizes) {
							toDelete.push(idx);
						} else {
							spec.css += " " + CSS_UNSELECTED;
						}
					}
					baseSizes.forEach(function(baseSize) {
						if (columnUrl == baseSize.$URL) {
							spec.css += " " + CSS_SIZE_BASE;
						}
					});
				}, this);
			} else {
				// For 2D chart remove fully unselected column if not DisplayAllSizes mode
				_.each(specs, function(spec, idx) {
					var columnUrl = spec.columnUrl;
					if (!_.some(selectedSizes, function(size) {
						var node = ctx.cache.getNode(size)
						var s1 = node.Dimension1Size;
						return (s1 == columnUrl);
					})) {
						if (!view.sizeChartDisplayAllSizes) {
							toDelete.push(idx);
						} else {
							spec.css += " " + CSS_UNSELECTED;
						}
					};
					baseSizes.forEach(function(baseSize) {
						if (columnUrl == baseSize.Dimension1Size) {
							spec.css += " " + CSS_SIZE_BASE;
						}
					});
				});
				// For 2D chart remove fully unselected matrix row if not DisplayAllSizes mode
				this._unselectedDim2 = [];
				this._unselectedDim2Indices = [];
				if (!view.sizeChartDisplayAllSizes || bBaseSize) {
					this._unselectedDim2 = this._dim2Sizes.slice(); // clone array
					const shownSizeNodes = bBaseSize ? baseSizes : ctx.cache.getNodes(selectedSizes);
					shownSizeNodes.forEach((s) => {
						const dim2Size = s.Dimension2Size;
						const ix = this._unselectedDim2.indexOf(dim2Size);
						if (ix > -1) {
							this._unselectedDim2.splice(ix, 1);
						}
					});
				}
			}
			toDelete.reverse(); // Start deleting from the back of array to preserve element order
			toDelete.forEach((idx) => specs.splice(idx, 1));

			if (subSizeRanges.length > 1) {
				// if composite size range, add dividers to separate sub size ranges
				var n = 0;

				_.each(subSizeRanges, function(sr) {
					n += bBaseSize ? 1 : sr.Sizes.length;

					// shift divider to the right position if some columns were hidden in non-sizeChartDisplayAllSizes mode
					toDelete = toDelete.filter((e) => {
						if (e < n) {
							n--;
							return false;
						} else {
							return true;
						}
					})

					// insert divider column
					if (n < specs.length) {
						specs.splice(n++, 0, new Spec({
							heading: _spacerHeading,
							headerParams: {
								className: constants.CSS_TABLE_COLUMNS,
							},
							valueParams: {
								className: html.CSS_NOT_APPLICABLE
							}
						}));
					}
				});
			}

			// For SizeChart with 2-d SizeRange, and not 'Display Base Size Only', add the Dimension2 Sizes header column
			if (this._isTwoDim && specs.length) {
				specs.unshift(new Spec({
					css: constants.CSS_TABLE_COLUMNS,
					heading: h,
					headerParams: {
						innerText: locale.getEnumName(this._dim2Type)
					},
					index: DIM2_HEADER_INDEX
				}));
			}

			return specs;
		},

		shouldRenderLoneSubheader: function(/*Spec*/subSpec) {
			// summary: return true if header is just showing the name of the nodes along the matrix path
			return subSpec.heading.m_id != "Increments";
		},

		/**
		 * Cache the SizeChartRevision's size vectors for optimization
		 * @param {Context} ctx
		 */
		_cacheSizesVectors: function(ctx) {
			var _sizeChartRevision = this._getSizeChartRevision(ctx);
			this._sizeChartRevision = _sizeChartRevision;
			this._sizeChartRevisionData = new _SizeChartRevision(ctx.cache, _sizeChartRevision);

			this._sizes = _sizeChartRevision.Sizes;
			this._dim1Sizes = _sizeChartRevision.SizeChartDimension1Sizes;
			this._dim2Sizes = _sizeChartRevision.SizeChartDimension2Sizes;
			this._isTwoDim = !_.isEmpty(this._dim2Sizes);
			this._dim1Type = _sizeChartRevision.Dimension1Type;
			if (this._isTwoDim) {
				this._dim2Type = _sizeChartRevision.Dimension2Type;
			}
		},

		_getColumnNodes: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return nodes to be shown as columns
			const {cache, view} = ctx;
			if (view.sizeChartPrintBaseSize) {
				const baseSizes = view.getBaseSizes();
				return view._isTwoDim() ? [cache.getNode(baseSizes[0].Dimension1Size)] : baseSizes;
			} else {
				return view.getDim1Sizes();
			}
		},

		// override csi.table.matrix.Path#_getSpecIndices
		_getSpecIndices: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
			// summary: return list of indices to apply with spec (Dimension1 Size) to given node (SizeChartDimension)

			if (this._isTwoDim) {
				var apparelDimension = ctx.cache.getNode(nodes[0].Actual);
				var hasMatchingDimensionDependencies = apparelDimension && _.contains(apparelDimension.DimensionDependencies, this._dim2Type);

				if (spec.index == DIM2_HEADER_INDEX && !hasMatchingDimensionDependencies) {
					// For Not 2-Dimensional POMs, Dimension 2 Header should Appear Blank
					return [this.INVALID_INDEX];
				}
				else if (spec.index != DIM2_HEADER_INDEX && hasMatchingDimensionDependencies) {
					// Display 2-dimensional Increments matrix if the ApparelDimension has matching Dimension2 type dependency
					var indices = this._getTwoDimSpecIndices(ctx, spec.index);
					return this._unselectedDim2Indices.length ? indices.filter((num, idx) => this._unselectedDim2Indices.indexOf(idx) < 0) : indices;
				}
			}

			return this.inherited(arguments);
		},

		_getSpecNodes: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
			if (spec.index == DIM2_HEADER_INDEX) {
				// Display 2-dimensional Increments matrix if the ApparelDimension has matching Dimension2 type dependency
				var apparelDimension = ctx.cache.getNode(nodes[0].Actual);
				if (apparelDimension && _.contains(apparelDimension.DimensionDependencies, this._dim2Type)) {
					// Filter the 2nd dimension sizes by what selected on the switchboard
					var selectedDim2Sizes = ctx.view.selectedDim2Sizes ? ctx.view.selectedDim2Sizes : this._dim2Sizes;
					var unselectedDim2 = this._unselectedDim2;
					this._unselectedDim2Indices = [];
					if (unselectedDim2.length) {
						selectedDim2Sizes = selectedDim2Sizes.filter((size, idx) => {
							if (unselectedDim2.indexOf(size) >= 0) {
								this._unselectedDim2Indices.push(idx);
								return false;
							} else {
								return true;
							}
						});
					}
					return ctx.cache.getNodes(selectedDim2Sizes);
				}
			}

			return null;
		},

		_getTwoDimSpecIndices: function(/*Context*/ctx, /*Integer*/dim1SizeIndex) {
			//summary: (1) If not built yet, build the 2-dim spec indeces in form of vector of vectors [dim1-index, dim2-index]
			// (2) Store it on ctx.view for performances
			// (3) then returns the spec indices for that particular spec

			if (ctx.view.twoDimSpecIndices) {
				return ctx.view.twoDimSpecIndices[dim1SizeIndex];
			}

			// Not cached yet, then we have to build and store the vector of vectors on ctx.view
			var indices = ctx.view.twoDimSpecIndices = [];

			var cache = ctx.cache;
			var selectedDim2Sizes = ctx.view.selectedDim2Sizes ? ctx.view.selectedDim2Sizes : this._dim2Sizes;
			var mapDim2Sizes = csi.hashArray(selectedDim2Sizes, null, Number);

			var specIndices;
			var iDim1Size;
			var j;
			var k;
			for (iDim1Size = 0; iDim1Size < this._dim1Sizes.length; iDim1Size++) {
				specIndices = [];  // spec indices for one Spec (one column)

				// Initialize it to the so that each Dim1Size x Dim2Size has a cell, the holes will have indices of default
				for (k = 0; k < selectedDim2Sizes.length; k++) {
					specIndices[k] = this.INVALID_INDEX;
				}

				var specDim1Size = this._dim1Sizes[iDim1Size];
				for (j = 0; j < this._sizes.length; j++) {
					var curProductSize = cache.getNode(this._sizes[j]);
					var curIncSizeDim1Size = curProductSize.Dimension1Size;
					var curIncSizeDim2Size = curProductSize.Dimension2Size;

					if (specDim1Size == curIncSizeDim1Size) {
						var dim2Index = mapDim2Sizes[curIncSizeDim2Size];
						if (dim2Index != null) {
							specIndices[dim2Index] = j;
						}
					}
				}

				indices[iDim1Size] = specIndices;
			}
			ctx.view.twoDimSpecIndices = indices;

			return indices[dim1SizeIndex];
		},

		_renderSpec: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes, /*Number*/nColumn, /*DOMNode*/td) {
			// summary: render spec to given cell
			var hOriginal;

			// render the leading column - Dimension2 Sizes
			if (spec.index == DIM2_HEADER_INDEX) {
				hOriginal = spec.heading;

				if (!ctx.view.productSizeHeading) {
					ctx.view.productSizeHeading = lang.mixin(new Heading(), {m_prop: xml.PROP_NAME, m_right: xml.RIGHT_NONE});
					ctx.view.productSizeHeading.resolveHeading(ctx.defView.getLocaleMap(), null, reflection.getClass("ProductSize"));
				}
				spec.heading = ctx.view.productSizeHeading;
			}

			this.inherited(arguments);

			if (hOriginal) {
				spec.heading = hOriginal;
			}
		},

		/**
		 * Determine if given dimension shold bw shown as 2D matrix in this Dimension Table
		 * @param {Object} dimension
		 * @return {boolean} return true if given dimension should be shown as 2D Matrix in this Dimension Table
		 *
		 * The size chart revision has Dimension1Type (enum) and Dimension2Type (enum). Each ApparelDimension you add to a size chart has
		 * DimensionDependencies (enumlist). Display the 2D matrix in these cases:
		 * 	- The ApparelDimension's DimensionDependencies list matches the Size Chart Rev Dimension1Type, Dimension2Type (same values, same order)
		 * 	- The ApparelDimension has one Dimension Dependency and it is the same as the Size Chart Rev Dimension2Type
		 * All other cases should display as a single row.
		 */
		_is2DMatrix: function(dimension) {
			if (!dimension)
				return false; // must be special
			var dependencies = dimension.DimensionDependencies
			if ((dependencies.length == 1) && (dependencies[0] == this._dim2Type)) {
				return true;
			}
			if (dependencies.length == 2 && dependencies.includes(this._dim1Type) && dependencies.includes(this._dim2Type)) {
				return true;
			}
			return false;
		},

		/**
		 * @override
		 *   For Absolute mode unselected cell should be empty, do not render the content, apply css for background
		 *   For not Absolute view mode unselected cell should have special css styling
		 */
		_renderCellContent: function(ctx, h, td, columnSpec, index) {
			var css = "";
			var bHidden = false;
			var columnIndex = columnSpec.columnIndex;
			// For 2D Size Charts use special styling for non Title cells (Title cell columnIndex == null)
			if ((columnIndex != null) && (this._isTwoDim)) {
				// Size chart might have multiple dimensions and every dimension inside 2D Size Chart might be 1D or 2D
				// Check to see if current row is 1D or 2D dimension. Assuming that 2D Dimension has Dimension Dependencies
				var cache = ctx.cache;
				var dimension = cache.getNode(ctx.getNode().Original);
				if (this._is2DMatrix(dimension)) {
					// Find unselected cells and draw them using special CSS (CSS_UNSELECTED)
					var selectedSizes = this._sizeChartRevision.SelectedSizes;
					var view = ctx.view;
					// Find if size in this cell is unselected (not in Selected Sizes)
					if (!selectedSizes.includes(this._sizes[index])) {
						// For non-sizeChartDisplayAllSizes mode unselected cell should be empty, do not render the content, apply css for background
						if (!view.sizeChartDisplayAllSizes) {
							bHidden = true;
							css = CSS_UNSELECTED;
						} else {
							// For sizeChartDisplayAllSizes view mode unselected cell should have special css styling
							css = CSS_UNSELECTED;
						}
					}
					if (css) {
						domClass.add(td, css);
					}
				}
			}
			if (!bHidden) {
				// Actual cell rendering call
				this.inherited(arguments);
			}
		}


	});
});